import React from "react";
import {
  ChevronLeft,
  Users,
  Copy,
  Radio as RadioIcon,
  Square,
} from "lucide-react";
import { ConsoleMoreMenu, CrewModeSelect } from "./ConsoleParts";

/**
 * ConsoleTopToolbar — the top status bar for Full Studio.
 *
 * Surfaces:
 *   - Exit back to Director's Mode
 *   - Stream title + active scene number
 *   - On-Air + Link LEDs driven by `bcStatus`
 *   - Start / End broadcast control
 *   - Live viewer count
 *   - Tap In Crew engagement selector
 *   - Invite-code copy button
 *   - More menu (comments, FX, record, chat toggle)
 *
 * Pulled out of DirectorConsole.jsx to keep that page under its line
 * budget. Pure presentation — every action is a callback prop; no
 * internal state.
 */
export default function ConsoleTopToolbar({
  stream,
  sceneActive,
  bcStatus,
  viewerCount,
  recording,
  commentsEnabled,
  chatOpen,
  onExit,
  onStartBroadcast,
  onEndBroadcast,
  onCopyInvite,
  onToggleComments,
  onOpenFx,
  onStartRecording,
  onStopRecording,
  onToggleChat,
}) {
  return (
    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10 bg-[#0A0D14] flex-wrap">
      <button
        data-testid="console-exit"
        onClick={onExit}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white text-xs font-bold"
        title="Back to Director's Mode"
      >
        <ChevronLeft className="w-4 h-4" /> Exit
      </button>

      <div className="w-px h-6 bg-white/10" />

      <div className="min-w-0">
        <div className="font-display font-black text-sm leading-tight truncate max-w-[30ch]">{stream.title}</div>
        <div className="text-[9px] uppercase tracking-[0.2em] text-white/40">
          Scene <span className="text-tv-brand">{sceneActive}</span>
        </div>
      </div>

      <div className="flex-1" />

      {/* On-Air + Link LEDs */}
      <div className="flex items-center gap-3 px-3 py-1 rounded-lg bg-black/40 border border-white/10">
        <div className="flex flex-col items-center gap-0.5">
          <div
            data-testid="console-led-onair"
            className={`w-2.5 h-2.5 rounded-full transition ${
              bcStatus === "live" ? "bg-tv-live shadow-[0_0_10px_rgba(255,0,64,0.9)] tv-pulse" : "bg-white/10"
            }`}
          />
          <span className="text-[8px] uppercase tracking-wider font-black text-white/50">On Air</span>
        </div>
        <div className="flex flex-col items-center gap-0.5">
          <div
            data-testid="console-led-link"
            className={`w-2.5 h-2.5 rounded-full transition ${
              bcStatus === "reconnecting"
                ? "bg-yellow-400 tv-pulse"
                : bcStatus === "live"
                ? "bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]"
                : "bg-white/10"
            }`}
          />
          <span className="text-[8px] uppercase tracking-wider font-black text-white/50">Link</span>
        </div>
      </div>

      {/* Start / End broadcast */}
      {bcStatus !== "live" ? (
        <button
          data-testid="console-start-btn"
          onClick={onStartBroadcast}
          className="bg-tv-live hover:bg-red-600 text-white font-display font-black text-xs uppercase tracking-wider px-4 py-2 rounded-lg flex items-center gap-1.5 shadow-lg shadow-red-500/30"
        >
          <RadioIcon className="w-3.5 h-3.5" /> Start
        </button>
      ) : (
        <button
          data-testid="console-end-btn"
          onClick={onEndBroadcast}
          className="bg-white/10 hover:bg-white/20 text-white font-display font-black text-xs uppercase tracking-wider px-4 py-2 rounded-lg flex items-center gap-1.5 border border-white/20"
        >
          <Square className="w-3.5 h-3.5 fill-current" /> End
        </button>
      )}

      <span className="inline-flex items-center gap-1 bg-white/5 border border-white/10 text-white text-xs font-semibold px-2.5 py-1 rounded">
        <Users className="w-3 h-3" /> {viewerCount.toLocaleString()}
      </span>

      {/* Tap In Crew engagement level for THIS stream */}
      <CrewModeSelect streamId={stream.id} />

      <button
        data-testid="console-invite"
        onClick={onCopyInvite}
        className="inline-flex items-center gap-1 bg-white/5 hover:bg-white/10 text-white text-xs font-semibold px-2.5 py-1 rounded border border-white/10"
        title="Copy guest invite code"
      >
        <Copy className="w-3 h-3" /> {stream.invite_code}
      </button>

      <ConsoleMoreMenu
        commentsEnabled={commentsEnabled}
        onToggleComments={onToggleComments}
        onOpenFx={onOpenFx}
        recording={recording}
        onStartRecording={onStartRecording}
        onStopRecording={onStopRecording}
        chatOpen={chatOpen}
        onToggleChat={onToggleChat}
      />
    </div>
  );
}
